import { useState, useRef } from 'react';
import clsx from 'clsx';
import styles from './actionbar.module.css';
import { motion } from 'motion/react';
import * as Sentry from '@sentry/react';
import {Flex, IconButton, Dropdown, Divider, notification, Button, Tooltip, icon as iconSize, iconWeight, space, Icon, useBreakpoint} from '@nula/core-ui';
import { useUIStore } from '@/stores/use-ui-store';
import { useWorkspaceService } from '@/lib/workspace/use-workspace-service';
import { useWorkspaceDoc } from '@/lib/sync/hooks/use-workspace-doc';
import { useGatedActions } from '@/lib/sharing/hooks/use-gated-actions';
import { useCanEdit } from '@/lib/sharing/hooks/use-can-edit';
import { useAccess } from '@/lib/sharing/hooks/use-access';
import { SharedBadge } from '@/lib/sharing/components/shared-badge';
import { ShareDialog } from '@/lib/sharing/components/share-dialog';
import { ConfirmMenuItem } from '@/components/confirm-menu-item/confirm-menu-item';
import { DocumentInfoPopover } from './document-info-popover';
import { CollectionsPopover } from './collections-popover';
import { PresenceIndicator } from '@/lib/sync/collaboration/presence-indicator';
import { getEntityKindMeta } from '@/utils/entity-kind-meta';

interface ActionbarProps {
  documentId?: string;
  style?: React.CSSProperties;
  className?: string;
  leftActions?: React.ReactNode;
  rightActions?: React.ReactNode;
  /**
   * Scope presence to a sub-view of a multi-surface entity (e.g. the active
   * document surface). Forwarded to PresenceIndicator. Omit for single-surface
   * entities (unscoped presence, legacy behavior).
   */
  presenceViewId?: string;
}

export function Actionbar({ documentId, style, className, leftActions, rightActions, presenceViewId }: ActionbarProps) {
  const bp = useBreakpoint();
  const toggleFindBar = useUIStore((s) => s.toggleFindBar);
  const setCommentsPanelOpen = useUIStore((s) => s.setCommentsPanelOpen);

  const service = useWorkspaceService();
  const [shareOpen, setShareOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [collectionsOpen, setCollectionsOpen] = useState(false);
  const moreButtonRef = useRef<HTMLDivElement>(null);
  const doc = useWorkspaceDoc(documentId ?? null);
  const documentTitle = doc?.title ?? 'Untitled';
  const tags = doc?.tags ?? [];
  const canEdit = useCanEdit(documentId ?? null);
  const access = useAccess(documentId ?? null);

  const handleMoveToTrash = () => {
    if (!documentId) return;
    const id = documentId;
    const title = documentTitle;
    // Archiving fires the entity store's _onEntityRemoved, wired to
    // closeEntityAcrossAllPanels -> closeTab, which activates the adjacent
    // (previous) tab. We deliberately do NOT navigate to 'home' here — that
    // would override the reducer and dump the user on the home page instead of
    // returning them to where they came from (PROD-3).
    service.archiveDoc(id, true);
    let instance: { destroy: () => void } | null = null;
    instance = notification.success({
      message: 'Moved to trash',
      description: title,
      btn: (
        <Button
          size="sm"
          variant="text"
          className={styles.undoButton}
          onClick={() => { service.archiveDoc(id, false); instance?.destroy(); }}
        >
          Undo
        </Button>
      ),
      duration: 5000,
    });
  };

  const handleAddTag = (tag: string) => {
    if (documentId) service.addTag(documentId, tag);
  };

  const handleRemoveTag = (tag: string) => {
    if (documentId) service.removeTag(documentId, tag);
  };

  const handleAddCollection = (collectionId: string) => {
    if (documentId) service.addToCollection(documentId, collectionId);
  };

  const handleRemoveCollection = (collectionId: string) => {
    if (documentId) service.removeFromCollection(documentId, collectionId);
  };


  // Gated actions — items filtered by the user's permission level.
  // Version history (view + restore + delete) requires edit.
  // Move to Trash requires delete (owner only).
  const editorItems = useGatedActions(
    documentId,
    [
      {
        key: 'history',
        require: 'edit' as const,
        label: 'Version History',
        icon: <Icon name="history" size={iconSize.md} weight={iconWeight.light} />,
        onClick: () => useUIStore.getState().setVersionHistoryOpen(true),
      },
    ],
  );
  const destructiveItems = useGatedActions(
    documentId,
    [
      {
        key: 'trash',
        require: 'delete' as const,
        label: 'Move to Trash',
        icon: <Icon name="delete" size={iconSize.md} weight={iconWeight.light} />,
        danger: true,
        onClick: handleMoveToTrash,
      },
      {
        key: 'remove-self',
        require: 'removeSelf' as const,
        icon: <Icon name="logout" size={iconSize.md} weight={iconWeight.light} />,
        onClick: () => {},
        label: (
          <ConfirmMenuItem
            label="Remove from my workspace"
            confirmLabel="Click to confirm"
            onConfirm={() => { if (documentId) service.leaveEntity(documentId).catch((err) => Sentry.captureException(err)); }}
          />
        ),
      },
    ],
  );

  const moreMenuItems = [
    { key: 'info', label: 'Document Info', icon: <Icon name="info" size={iconSize.md} weight={iconWeight.light} />, onClick: () => { setTimeout(() => setInfoOpen(true), 100); } },
    { key: 'find', label: 'Find in Document', icon: <Icon name="search" size={iconSize.md} weight={iconWeight.light} />, onClick: toggleFindBar },
    { key: 'd1', label: '', type: 'divider' as const },
    { key: 'comments', label: 'Comments', icon: <Icon name="chat_bubble" size={iconSize.md} weight={iconWeight.light} />, onClick: () => setCommentsPanelOpen(true) },
    ...editorItems,
    ...(destructiveItems.length > 0 ? [{ key: 'd2', label: '', type: 'divider' as const }, ...destructiveItems] : []),
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      className={clsx(styles.wrapper, className)}
      style={style}
    >
      {/* Identity/presence leads the toolbar, separated from the actions by a
          divider — presence is "who", the buttons are "do". */}
      <SharedBadge access={access} />
      {documentId && <PresenceIndicator entityId={documentId} viewId={presenceViewId} />}
      <Divider orientation="vertical" className={styles.divider} />

      {bp.md ? (
        <Flex align="center" gap={space[1]}>
          {/* leftActions lead the toolbar (first item) — ahead of Share. */}
          {leftActions && (
            <Flex align="center" gap={space[1]}>{leftActions}</Flex>
          )}
          <Tooltip title="Share" placement="bottom">
            <IconButton aria-label="Share" onClick={() => setShareOpen(true)} className={styles.iconButton}>
              <Icon name="share" size={iconSize.md} weight={iconWeight.light} />
            </IconButton>
          </Tooltip>
          {canEdit && (
            <Flex align="center" className={styles.relative}>
              <Tooltip title="Collections" placement="bottom">
                <IconButton aria-label="Collections" onClick={() => setCollectionsOpen((v) => !v)} className={styles.iconButton}>
                  <Icon name={getEntityKindMeta('collection').icon} size={iconSize.md} weight={iconWeight.light} />
                </IconButton>
              </Tooltip>
              {documentId && (
                <CollectionsPopover
                  open={collectionsOpen}
                  onClose={() => setCollectionsOpen(false)}
                  entityId={documentId}
                  onAddCollection={handleAddCollection}
                  onRemoveCollection={handleRemoveCollection}
                />
              )}
            </Flex>
          )}
          <Dropdown trigger={['click']} placement="bottom-end" menu={{ items: moreMenuItems }}>
            <Flex align="center" className={styles.relative} ref={moreButtonRef}>
              <IconButton aria-label="More Options" className={styles.iconButton}>
                <Icon name="more_horiz" size={iconSize.md} weight={iconWeight.light} />
              </IconButton>
              {documentId && (
                <DocumentInfoPopover
                  entityId={documentId}
                  open={infoOpen}
                  onClose={() => setInfoOpen(false)}
                  tags={tags}
                  onAddTag={handleAddTag}
                  onRemoveTag={handleRemoveTag}
                  onAddCollection={handleAddCollection}
                  onRemoveCollection={handleRemoveCollection}
                />
              )}
            </Flex>
          </Dropdown>
        </Flex>
      ) : (
        <Flex align="center" gap={space[1]}>
          {leftActions}
          <Dropdown
          trigger={['click']}
          placement="bottom-end"
          menu={{
            items: [
              { key: 'share', label: 'Share', icon: <Icon name="share" size={iconSize.md} weight={iconWeight.light} />, onClick: () => setShareOpen(true) },
              ...(canEdit ? [{ key: 'collections', label: 'Collections', icon: <Icon name={getEntityKindMeta('collection').icon} size={iconSize.md} weight={iconWeight.light} />, onClick: () => setCollectionsOpen(true) }] : []),
              { key: 'd0', label: '', type: 'divider' as const },
              ...moreMenuItems,
            ],
          }}
        >
          <Flex align="center" className={styles.relative} ref={moreButtonRef}>
            <IconButton aria-label="More actions" className={styles.iconButton}>
              <Icon name="more_horiz" size={iconSize.md} weight={iconWeight.light} />
            </IconButton>
            {documentId && (
              <DocumentInfoPopover
                entityId={documentId}
                open={infoOpen}
                onClose={() => setInfoOpen(false)}
                tags={tags}
                onAddTag={handleAddTag}
                onRemoveTag={handleRemoveTag}
              />
            )}
          </Flex>
        </Dropdown>
        </Flex>
      )}

      {rightActions && (
        <Flex align="center" gap={space[1]}>{rightActions}</Flex>
      )}

      {documentId && (
        <ShareDialog
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          resourceId={documentId}
          resourceTitle={documentTitle}
        />
      )}
    </motion.div>
  );
}
